
import { userService } from './userService';
import { UserProfile, LessonContent, CurriculumNode, SubjectType, ChatMessage, Language, GrowthProjection } from "../types";
import { invokeAI, streamAI, tryParsePartialJSON, addWavHeader, decodeBase64 } from "./ai/core";
import { getOfflineLesson, getOfflineChatReply } from "./ai/offlineFactory";

/**
 * Subject-specific progression data for offline fallback
 */
const OFFLINE_PROJECTIONS: Record<string, any> = {
  math: {
    titles: { zh: ["计算员", "几何家", "逻辑大师", "星际架构师"], en: ["Calculator", "Geometrician", "Logician", "Star Architect"] },
    stages: { zh: ["具体运算阶段", "数理逻辑构建期"], en: ["Concrete Operational", "Logical Construction"] }
  },
  science: {
    titles: { zh: ["观察员", "实验家", "研究员", "万物探索家"], en: ["Observer", "Experimenter", "Researcher", "Omni-Explorer"] },
    stages: { zh: ["现象认知阶段", "系统科学思维期"], en: ["Phenomena Phase", "Systemic Inquiry"] }
  },
  chinese: {
    titles: { zh: ["识字者", "朗读者", "创作者", "文化使者"], en: ["Reader", "Speaker", "Creator", "Cultural Envoy"] },
    stages: { zh: ["符号认知阶段", "文学审美共鸣期"], en: ["Symbolic Phase", "Literary Resonance"] }
  },
  english: {
    titles: { zh: ["发音员", "对话者", "演讲家", "全球领航员"], en: ["Pronouncer", "Speaker", "Orator", "Global Pilot"] },
    stages: { zh: ["语感习得阶段", "跨文化思辨期"], en: ["Linguistic Acquisition", "Cross-cultural Critical Thinking"] }
  },
  history: {
    titles: { zh: ["听书人", "记录者", "见证人", "文明观察者"], en: ["Listener", "Chronicler", "Witness", "Civilization Observer"] },
    stages: { zh: ["线性时间感知", "多元史观构建"], en: ["Linear Perception", "Historical Perspective"] }
  },
  art: {
    titles: { zh: ["涂鸦手", "调色师", "写生家", "视觉艺术家"], en: ["Doodler", "Colorist", "Sketcher", "Visual Artist"] },
    stages: { zh: ["感官探索阶段", "符号表现成熟期"], en: ["Sensory Exploration", "Symbolic Expression"] }
  },
  all: {
    titles: { zh: ["探索者", "进阶者", "领主", "贤者"], en: ["Explorer", "Advanced", "Lord", "Sage"] },
    stages: { zh: ["多元智能萌芽", "综合素养跨越期"], en: ["Multiple Intelligence", "Synthesized Growth"] }
  }
};

export const generateLessonContent = async (
  nodeId: string,
  topic: string,
  subject: SubjectType,
  user: UserProfile,
  lang: Language,
  onUpdate?: (partial: LessonContent) => void
): Promise<LessonContent | null> => {
  try {
    const cached = await userService.getGlobalLessonContent(nodeId, user.grade, lang);
    if (cached && cached.title) {
        if (onUpdate) onUpdate(cached);
        return cached;
    }
    
    if (onUpdate) {
       let accumulatedText = "";
       let lastParsed: LessonContent | null = null;

       await streamAI('generate-lesson', {
          topic, subject, grade: user.grade, age: user.age, interests: user.interests, userStats: user.stats, lang
       }, (chunk) => {
          accumulatedText += chunk;
          const partial = tryParsePartialJSON(accumulatedText);
          
          if (partial) { 
             // 即使 title 还没出来，只要有任何内容就开始更新 UI
             lastParsed = partial;
             onUpdate(partial);
          }
       });

       if (!lastParsed && accumulatedText.trim()) {
          const partial = tryParsePartialJSON(accumulatedText);
          if (partial) lastParsed = partial;
       }

       if (lastParsed) {
          await userService.saveGlobalLessonContent(nodeId, user.grade, lang, lastParsed);
          return lastParsed;
       }
       throw new Error("GENERATION_FAILED");
    } else {
       const data = await invokeAI('generate-lesson', {
         topic, subject, grade: user.grade, age: user.age, interests: user.interests, userStats: user.stats, lang
       });
       if (data && data.title) {
         await userService.saveGlobalLessonContent(nodeId, user.grade, lang, data);
         return data as LessonContent;
       }
       return null;
    }
  } catch (error: any) {
    const offline = getOfflineLesson(topic, subject, lang);
    if (onUpdate) onUpdate(offline);
    return offline;
  }
};

export const generateCurriculumExpansion = async (
  subject: SubjectType,
  currentNodes: CurriculumNode[],
  user: UserProfile,
  lang: Language
): Promise<CurriculumNode[]> => {
  try {
    const lastNode = currentNodes[currentNodes.length - 1];
    const data = await invokeAI('expand-curriculum', {
      subject, lastNodeTitle: lastNode.title, lastNodeDesc: lastNode.desc, lastNodeLevel: lastNode.level, grade: user.grade, interests: user.interests, userStats: user.stats, lang
    });
    if (data && data.nodes) {
       return data.nodes.map((n: any, i: number) => ({
        id: `${subject[0]}${Date.now()}${i}`,
        title: n.title, desc: n.desc, level: lastNode.level + (i > 1 ? 1 : 0), prev: i === 0 ? lastNode.id : `${subject[0]}${Date.now()}${i-1}`
      }));
    }
    return [];
  } catch (error) {
    return [];
  }
};

export const generateParentReport = async (user: UserProfile, lang: Language): Promise<any> => {
  try {
    return await invokeAI('generate-report', { stats: user.stats, grade: user.grade, lang });
  } catch (e) {
    return { analysis: lang === 'zh' ? "（离线分析）表现热情！" : "(Offline Analysis) Great enthusiasm!", suggestions: [lang === 'zh' ? "保持每日登录" : "Maintain daily streak"] };
  }
};

export const generateGrowthProjection = async (user: UserProfile, subject: SubjectType | 'all', lang: Language): Promise<GrowthProjection> => {
  try {
    return await invokeAI('growth-projection', {
      subject, level: user.level, grade: user.grade, score: subject === 'all' ? Math.floor(user.xp / 100) : (user.stats[subject] || 0), lang
    });
  } catch (e) {
    const isZh = lang === 'zh';
    const l = isZh ? 'zh' : 'en';
    const config = OFFLINE_PROJECTIONS[subject] || OFFLINE_PROJECTIONS.all;
    const titles = config.titles[l];
    const stages = config.stages[l];

    return {
       timeline: {
          current: `Lv.${user.level} ${titles[0]}`, 
          threeMonth: `Lv.${user.level + 3} ${titles[1]}`, 
          oneYear: `Lv.${user.level + 12} ${titles[2]}`, 
          threeYear: `Lv.${user.level + 30} ${titles[3]}`
       },
       cognitive_stage: {
          name: stages[0],
          focus: isZh ? "领域核心建模" : "Domain Core Modeling",
          desc: isZh ? "正在通过本地知识库构建基础认知结构。" : "Building basic cognitive structures via local knowledge base."
       }
    };
  }
};

export const generateTTS = async (text: string): Promise<string | null> => {
  if (!text) return null;
  try {
    const cachedBase64 = await userService.getTTSCache(text);
    if (cachedBase64) {
        const blob = addWavHeader(decodeBase64(cachedBase64), 24000);
        return URL.createObjectURL(blob);
    }
    const data = await invokeAI('generate-tts', { text });
    if (!data?.audioContent) return null;
    userService.saveTTSCache(text, data.audioContent);
    const blob = addWavHeader(decodeBase64(data.audioContent), 24000);
    return URL.createObjectURL(blob);
  } catch (e: any) {
    return null;
  }
};

export const chatWithNono = async (history: ChatMessage[], message: string, user: UserProfile, lang: Language): Promise<string> => {
  try {
    const data = await invokeAI('chat', { history, message, grade: user.grade, name: user.name, age: user.age, interests: user.interests, lang });
    return data.reply || "...";
  } catch (e) {
    return getOfflineChatReply(message, lang);
  }
};
